import "./TestIcdCodesEditComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from "formik";
import {ITableColumn} from "../../../../../shared/models/table.model";
import CheckboxComponent from "../../../../../shared/components/core/checkbox/CheckboxComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import {DeleteForever} from "@mui/icons-material";
import _ from "lodash";
import {CommonService} from "../../../../../shared/services";
import FormikRadioButtonGroupComponent
    from "../../../../../shared/components/core/formik-radio-button-group/formikRadioButtonGroupComponent";
import {Grid} from "@material-ui/core";
import SearchComponent from "../../../../../shared/components/search/SearchComponent";
import TableV2Component from "../../../../../shared/components/table-components/table-v2/TableV2Component";
import DrawerComponent from "../../../../../shared/components/drawer/drawerComponent";
import HeadingComponent from "../../../../../shared/components/heading/headingComponent";
import TableWrapperV2Component
    from "../../../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import {APIConfig} from "../../../../../constants";

interface TestIcdCodesEditComponentProps {
    testDetails: any;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const testIcdValidationSchema = Yup.object().shape({
    isIcdCodeRequired: Yup.boolean().required(),
    searchString: Yup.string(),
    icdCodes: Yup.array().when('isIcdCodeRequired', {
        is: true,
        then: Yup.array()
            .of(Yup.number().required())
            .min(1, "Select at least one ICD")
            .required("Select ICD"),
        otherwise: Yup.array()
    })
});

interface testIcdCodeSchema {
    isIcdCodeRequired: boolean,
    searchString: string,
    icdCodes: number[]
}

const testIcdInitialValues: testIcdCodeSchema = {
    isIcdCodeRequired: false,
    searchString: "",
    icdCodes: []
}


const TestIcdCodesEditComponent = (props: TestIcdCodesEditComponentProps) => {
    const {testDetails, onUpdateComplete, onEditCancel} = props;

    const [initialFormValue, setInitialFormValue] = useState<FormikValues>(testIcdInitialValues)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedIcdList, setSelectedIcdList] = useState<any[]>([]);

    const [drawerTableFilter, setDrawerTableFilter] = useState({
        search: ""
    })

    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const columns: ITableColumn[] = [
        {
            id: 'select',
            label: <></>,
            hideInColumnPreferences: true,
            isDefaultColumn: true,
            fixed: "left",
            width: '50',
            content: (item: any) => (
                <CheckboxComponent
                    name={"sample_select"}
                    size={"small"}
                    // disabled={sendOutApplicableSamples?.findIndex((sample: ISample) => sample.id === item.id) === -1}
                    disableRipple={true}
                    checked={selectedIcdList?.findIndex((sample: any) => sample?.id === item?.id) > -1}
                    onUpdate={(isChecked: boolean) => {
                        handleSelectDeselectSample(isChecked, item);
                    }}/>
            )
        },
        {
            id: "icdCode",
            label: "ICD Code",
            width: 100,
            isDefaultColumn: true, align: "center"
        },
        {
            id: "description",
            label: "Description",
            width: 300,
            isDefaultColumn: true, align: "left"
        },
    ]

    const selectedTableColumns: ITableColumn[] = [
        {
            id: "icdCode",
            label: "ICD Code",
            width: 100,
            isDefaultColumn: true, align: "center"
        },
        {
            id: "description",
            label: "Description",
            width: 300,
            isDefaultColumn: true, align: "left"
        },
        {
            id: 'action',
            label: "Action",
            hideInColumnPreferences: true,
            isDefaultColumn: true,
            width: '50',
            content: (item: any) => (
                <ButtonComponent
                    color={"error"}
                    variant={"icon"}
                    handleClick={() => {
                        const selectedIcdlistAfterDelete = selectedIcdList.filter((icd: any) => icd.id !== item.id);
                        const icdCodesAfterDelete = selectedIcdlistAfterDelete.map((icd: any) => icd.id)
                        formikRef.current?.setFieldValue("icdCodes", icdCodesAfterDelete)
                        setSelectedIcdList(selectedIcdlistAfterDelete)
                    }}
                >
                    <DeleteForever/>
                </ButtonComponent>
            )
        }
    ]

    const handleSelectDeselectSample = useCallback((isChecked: boolean, icdCode: any) => {
        const updatedSampleList = _.cloneDeep(selectedIcdList);
        if (isChecked) {
            updatedSampleList?.push(icdCode);
        } else {
            const icdID = icdCode?.id;
            updatedSampleList?.splice(updatedSampleList?.findIndex(item => item?.id === icdID), 1);
        }
        setSelectedIcdList(updatedSampleList);
        const icdCodes = updatedSampleList.map((item) => item?.id)
        formikRef?.current && formikRef?.current?.setFieldValue("icdCodes", icdCodes)
    }, [selectedIcdList]);

    const onSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        const payload = _.cloneDeep(values);
        if (!payload.isIcdCodeRequired) {
            payload.icdCodes = []
        }
        delete payload.searchString;
        CommonService._test.addTestIcdCode(testDetails?.id, payload)
            .then((response: any) => {
                setSubmitting(false);
                const updatedTestDetails = {
                    ...testDetails,
                    icdCodesDetails: response?.data?.icdCodesDetails,
                    isIcdCodeRequired: response?.data?.isIcdCodeRequired,
                    icdCodes: response?.data?.icdCodes,
                };
                onUpdateComplete(updatedTestDetails);
            }).catch((error: any) => {
            setSubmitting(false);
            CommonService._alert.showToast(error?.response?.data?.message, 'error');
        })
    }, [onUpdateComplete, testDetails]);

    useEffect(() => {
        setInitialFormValue((prev) => {
            return {
                ...prev,
                isIcdCodeRequired: testDetails?.isIcdCodeRequired || false,
                icdCodes: testDetails?.icdCodes || []
            }
        })
        setSelectedIcdList(testDetails?.icdCodesDetails || [])
    }, [testDetails]);

    return (
        <div className={'test-icd-codes-edit-component'}>
            <Formik
                validationSchema={testIcdValidationSchema}
                initialValues={initialFormValue}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                validateOnMount={true}
                innerRef={formikRef}
                onSubmit={onSubmit}
            >
                {({isSubmitting, values, dirty, isValid, setFieldValue, touched, errors, validateForm}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);

                    return (
                        <Form className={"test-panel-configuration-add-holder"}>
                            {/*{JSON.stringify(values)}*/}
                            <div className="t-form-controls-wrapper pdd-top-5">
                                <div className="d-flex align-items-center mrg-bottom-25">
                                    <div className="radio-button-title">Is ICD code required?</div>
                                    <Field
                                        name={'isIcdCodeRequired'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikRadioButtonGroupComponent
                                                    direction={"row"}
                                                    isValueBoolean={true}
                                                    titleKey={'title'}
                                                    valueKey={'code'}
                                                    options={CommonService._staticData.yesNoOptions}
                                                    formikField={field}
                                                />
                                            )
                                        }
                                    </Field>
                                </div>
                                {/*<div className={'horizontal-line'}></div>*/}
                                {
                                    values.isIcdCodeRequired && (
                                        <>
                                            {/*<FormSubTitleWithTooltipComponent title={"All Linked ICD Codes"}/>*/}
                                            <Grid container spacing={2} alignItems={"center"}>
                                                <Grid item md={6} lg={6}>
                                                    <Field name={"searchString"}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <SearchComponent label={""}
                                                                                 disabled={values.icdCodes.length < 1}
                                                                                 placeholder={"Search ICD Codes"}
                                                                                 onChange={(e) => {
                                                                                     setFieldValue("searchString", e)
                                                                                 }}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                                <Grid item md={3} lg={3}>
                                                    <ButtonComponent
                                                        color={"secondary"}
                                                        variant={"outlined"}
                                                        handleClick={() => setIsDrawerOpen(true)}
                                                        className={''}
                                                    >
                                                        Link ICD Code
                                                    </ButtonComponent>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )
                                }
                                {
                                    values?.isIcdCodeRequired && values.icdCodes.length > 0 && (
                                        <div className={'t-table-container mrg-top-25'}>
                                            <TableV2Component
                                                columns={selectedTableColumns}
                                                data={values.searchString ? selectedIcdList.filter((item) => item.icdCode.toLowerCase().includes(values.searchString.toLowerCase())) : selectedIcdList}
                                            />
                                        </div>
                                    )
                                }
                            </div>
                            <div className="form-action-section-edit">
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"secondary"}
                                    type={"button"}
                                    variant={"outlined"}
                                    disabled={isSubmitting}
                                    fullWidth={false}
                                    handleClick={() => {
                                        onEditCancel()
                                    }}
                                >
                                    Close
                                </ButtonComponent>

                                <ButtonComponent
                                    className={"width-25"}
                                    type="submit"
                                    color={"primary"}
                                    variant={"contained"}
                                    fullWidth={false}
                                    disabled={!(isValid && dirty)}
                                    isLoading={isSubmitting}
                                >
                                    Save Changes
                                </ButtonComponent>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
            <DrawerComponent
                className={"drawer-wrapper test-icd-code-drawer"}
                // title={'Lab Details'}
                open={isDrawerOpen}
                onClose={() => {
                    setIsDrawerOpen(false);
                }}
            >
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Select ICD Code(s)"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef?.current && formikRef?.current.setFieldValue("icdCodes", [])
                             setSelectedIcdList([])
                         }}
                    >
                        Clear all
                    </div>
                </div>
                <SearchComponent label={""} placeholder={"Search"} value={drawerTableFilter?.search}
                                 onChange={(e) => setDrawerTableFilter((prev) => {
                                     return {
                                         ...prev,
                                         search: e
                                     }
                                 })}/>
                <div className="table-list-container">
                    <TableWrapperV2Component
                        isPaginated={true}
                        columns={columns}
                        url={APIConfig.GET_ICD_CODE_LIST_TABLE.URL}
                        method={APIConfig.GET_ICD_CODE_LIST_TABLE.METHOD}
                        extraPayload={drawerTableFilter}
                        isColumnFilterVisible={true}
                        // onDataLoad={(data: any) => setData(data)}
                    />
                </div>
            </DrawerComponent>
        </div>
    );

};

export default TestIcdCodesEditComponent;
